// src/features/reporting/report-toolbar.tsx
import React from "react";
import { GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector } from '@mui/x-data-grid';
import { Box, IconButton, Tooltip, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import GetAppIcon from '@mui/icons-material/GetApp';
import { Badge } from '@mui/material';
import { ReportFilterConfig } from 'app-types';
import { FilterCondition } from './reporting-types';
import DateRangeFilter from './filters/DateRangeFilter';

interface ReportToolbarProps {
  onRefresh: () => void;
  onReset: () => void;
  onExport: () => void;
  isDirty: boolean;
  isWorking: boolean;
  dateRangeFilter?: ReportFilterConfig;
  filters: FilterCondition[];
  onFiltersChange: (newFilters: FilterCondition[]) => void;
  onFiltersInitialized?: () => void;
}

const ReportToolbar: React.FC<ReportToolbarProps> = ({
  onRefresh,
  onReset,
  onExport,
  isDirty,
  isWorking,
  dateRangeFilter,
  filters,
  onFiltersChange,
  onFiltersInitialized,
}) => {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" paddingX={1} gap={1} flexWrap="wrap">
      {/* Left side: Date range control + default toolbar buttons */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        {dateRangeFilter && !dateRangeFilter.isHidden && (
          <DateRangeFilter
            config={dateRangeFilter}
            filters={filters}
            isWorking={isWorking}
            onChange={onFiltersChange}
            onInitialized={onFiltersInitialized}
          />
        )}
        <GridToolbarColumnsButton {...({} as React.ComponentProps<typeof GridToolbarColumnsButton>)} />
        <GridToolbarFilterButton {...({} as React.ComponentProps<typeof GridToolbarFilterButton>)} />
        <GridToolbarDensitySelector {...({} as React.ComponentProps<typeof GridToolbarDensitySelector>)} />
        <Button
          size="small"
          startIcon={<GetAppIcon />}
          onClick={onExport}
          disabled={isWorking}
        >
          Export
        </Button>
      </Box>

      {/* Right side: Refresh and Reset buttons */}
      <Box display="flex" marginLeft="auto" alignItems="center">
        <Tooltip title={isDirty ? "Refresh (changes pending)" : "Refresh"}>
          <span>
            <IconButton onClick={onRefresh} color={isDirty ? "secondary" : "primary"} disabled={isWorking}>
              <Badge color="secondary" variant="dot" invisible={!isDirty}>
                <RefreshIcon />
              </Badge>
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Reset Filters and Sorts">
          <span>
            <IconButton onClick={onReset} color="default" disabled={isWorking}>
              <ClearIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ReportToolbar;
